import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Chip,
  alpha,
  Tooltip,
  Fade
} from '@mui/material';
import {
  Add,
  Delete,
  Business,
  Group,
  CheckCircle,
  Close
} from '@mui/icons-material';
import { fetchOrganizations, createOrganization, deleteOrganization, fetchClients } from '../api/admin';
import { Organization, OrganizationCreateInput } from '../types/admin';

const THEME = {
  BG_PRIMARY: "#000000",
  BG_SECONDARY: "#1a1a1a",
  BG_TERTIARY: "#2d2d2d",
  SURFACE_GLASS: "rgba(255, 255, 255, 0.03)",
  TEXT_PRIMARY: "#ffffff",
  TEXT_SECONDARY: "#b0b0b0",
  TEXT_MUTED: "#808080",
  ACCENT_PRIMARY: "#2196f3",
  ACCENT_SUCCESS: "#4caf50",
  ACCENT_ERROR: "#ff4444",
  BORDER_SUBTLE: "#333333",
  BORDER_DEFAULT: "#555555",
};

interface OrganizationsManagerProps {
  onClose?: () => void;
}

export const OrganizationsManager: React.FC<OrganizationsManagerProps> = ({ onClose }) => {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null);
  
  // Form state
  const [formData, setFormData] = useState<OrganizationCreateInput>({
    name: '',
    description: '',
    is_root: false
  });
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const loadOrganizations = useCallback(async () => {
    try {
      setLoading(true);
      const [orgsData, clientsData] = await Promise.all([
        fetchOrganizations(),
        fetchClients()
      ]);
      
      const orgsWithCounts = orgsData.map(org => ({
        ...org,
        clients_count: clientsData.filter(client => client.organization_id === org.id).length
      }));
      
      setOrganizations(orgsWithCounts);
      setError(null);   
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Impossibile caricare le organizzazioni');
      // Error('Error loading organizations:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadOrganizations();
  }, [loadOrganizations]);

  const handleCreateOrganization = async () => {
    if (!formData.name.trim()) {
      setFormError('Il nome è obbligatorio');
      return;
    }

    try {
      setSubmitting(true);
      setFormError(null);
      await createOrganization(formData);
      setCreateDialogOpen(false);
      setFormData({ name: '', description: '', is_root: false });
      await loadOrganizations();
    } catch (err: any) {
      setFormError(err.response?.data?.detail || 'Errore nella creazione');
      // Error('Error creating organization:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteOrganization = async () => {
    if (!selectedOrg) return;

    try {
      setSubmitting(true);
      await deleteOrganization(selectedOrg.id);
      setDeleteDialogOpen(false);
      setSelectedOrg(null);
      await loadOrganizations();
    } catch (err: any) {
      let errorMessage = 'Errore nell\'eliminazione';
      
      if (err.response?.data?.detail) {
        const detail = err.response.data.detail;
        
        if (detail.includes('Cannot delete root organization')) {
          errorMessage = '❌ Impossibile eliminare l\'organizzazione root. L\'organizzazione root non può essere eliminata.';
        } else if (detail.includes('Cannot delete users from root organization')) {
          errorMessage = '❌ Impossibile eliminare utenti dall\'organizzazione root.';
        } else {
          errorMessage = detail;
        }
      }
      
      setError(errorMessage);
      // Error('Error deleting organization:', err);
      setDeleteDialogOpen(false);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <CircularProgress sx={{ color: THEME.ACCENT_PRIMARY }} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ color: THEME.TEXT_PRIMARY, fontWeight: 600, mb: 1 }}>
            🌍 Gestione Organizzazioni
          </Typography>
          <Typography variant="body2" sx={{ color: THEME.TEXT_SECONDARY }}>
            Gestisci le organizzazioni del sistema
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Crea nuova organizzazione">
            <IconButton
              onClick={() => setCreateDialogOpen(true)}
              sx={{
                bgcolor: alpha(THEME.ACCENT_SUCCESS, 0.15),
                color: THEME.ACCENT_SUCCESS,
                '&:hover': {
                  bgcolor: alpha(THEME.ACCENT_SUCCESS, 0.25),
                  transform: 'scale(1.05)'
                },
                transition: 'all 0.2s'
              }}
            >
              <Add />
            </IconButton>
          </Tooltip>
          
          {onClose && (
            <Tooltip title="Chiudi">
              <IconButton 
                onClick={onClose}
                sx={{ 
                  color: THEME.TEXT_SECONDARY,
                  '&:hover': { 
                    bgcolor: alpha(THEME.ACCENT_ERROR, 0.1),
                    color: THEME.ACCENT_ERROR
                  }
                }}
              >
                <Close />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Organizations Grid */}
      <Grid container spacing={3}>
        {organizations.map((org) => (
          <Grid item xs={12} md={6} lg={4} key={org.id}>
            <Fade in timeout={300}>
              <Card
                sx={{
                  bgcolor: THEME.BG_SECONDARY,
                  border: `1px solid ${THEME.BORDER_SUBTLE}`,
                  borderRadius: 2,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    borderColor: THEME.ACCENT_PRIMARY,
                    transform: 'translateY(-4px)',
                    boxShadow: `0 8px 16px ${alpha(THEME.ACCENT_PRIMARY, 0.2)}`
                  }
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Business sx={{ color: THEME.ACCENT_PRIMARY, fontSize: 28 }} />
                      {org.is_root && (
                        <Chip
                          label="ROOT"
                          size="small"
                          sx={{
                            bgcolor: alpha(THEME.ACCENT_SUCCESS, 0.2),
                            color: THEME.ACCENT_SUCCESS,
                            fontWeight: 600,
                            fontSize: '0.7rem'
                          }}
                        />
                      )}
                    </Box>
                    <Tooltip title="Elimina organizzazione">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setSelectedOrg(org);
                          setDeleteDialogOpen(true);
                        }}
                        sx={{
                          color: THEME.ACCENT_ERROR,
                          '&:hover': { bgcolor: alpha(THEME.ACCENT_ERROR, 0.1) }
                        }}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>

                  <Typography variant="h6" sx={{ color: THEME.TEXT_PRIMARY, fontWeight: 600, mb: 1 }}>
                    {org.name}
                  </Typography>

                  {org.description && (
                    <Typography variant="body2" sx={{ color: THEME.TEXT_SECONDARY, mb: 2 }}>
                      {org.description}
                    </Typography>
                  )}

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
                    <Chip
                      icon={<Group />}
                      label={`${org.clients_count || 0} Clienti`}
                      size="small"
                      sx={{
                        bgcolor: alpha(THEME.ACCENT_PRIMARY, 0.1),
                        color: THEME.ACCENT_PRIMARY,
                        border: `1px solid ${alpha(THEME.ACCENT_PRIMARY, 0.3)}`
                      }}
                    />
                  </Box>

                  <Typography variant="caption" sx={{ color: THEME.TEXT_SECONDARY, display: 'block', mt: 2 }}>
                    Creata: {new Date(org.created_at).toLocaleDateString('it-IT')}
                  </Typography>
                </CardContent>
              </Card>
            </Fade>
          </Grid>
        ))}
      </Grid>

      {organizations.length === 0 && !loading && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Business sx={{ fontSize: 64, color: THEME.TEXT_SECONDARY, mb: 2 }} />
          <Typography variant="h6" sx={{ color: THEME.TEXT_SECONDARY }}>
            Nessuna organizzazione trovata
          </Typography>
        </Box>
      )}

      {/* Create Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => !submitting && setCreateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: THEME.BG_SECONDARY,
            border: `1px solid ${THEME.BORDER_SUBTLE}`,
            borderRadius: 2
          }
        }}
      >
        <DialogTitle sx={{ color: THEME.TEXT_PRIMARY }}>
          Nuova Organizzazione
        </DialogTitle>
        <DialogContent>
          {formError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {formError}
            </Alert>
          )}
          <TextField
            autoFocus
            label="Nome Organizzazione"
            fullWidth
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            sx={{
              mb: 2,
              mt: 1,
              '& .MuiOutlinedInput-root': {
                bgcolor: THEME.SURFACE_GLASS,
                color: THEME.TEXT_PRIMARY,
                '& fieldset': { borderColor: THEME.BORDER_SUBTLE },
                '&:hover fieldset': { borderColor: THEME.BORDER_DEFAULT },
                '&.Mui-focused fieldset': { borderColor: THEME.ACCENT_PRIMARY }
              },
              '& .MuiInputLabel-root': {
                color: THEME.TEXT_MUTED,
                '&.Mui-focused': { color: THEME.ACCENT_PRIMARY }
              }
            }}
            required
          />
          <TextField
            label="Descrizione"
            fullWidth
            multiline
            rows={3}
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                bgcolor: THEME.SURFACE_GLASS,
                color: THEME.TEXT_PRIMARY,
                '& fieldset': { borderColor: THEME.BORDER_SUBTLE },
                '&:hover fieldset': { borderColor: THEME.BORDER_DEFAULT },
                '&.Mui-focused fieldset': { borderColor: THEME.ACCENT_PRIMARY }
              },
              '& .MuiInputLabel-root': {
                color: THEME.TEXT_MUTED,
                '&.Mui-focused': { color: THEME.ACCENT_PRIMARY }
              }
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => {
              setCreateDialogOpen(false);
              setFormData({ name: '', description: '', is_root: false });
              setFormError(null);
            }}
            disabled={submitting}
            sx={{ color: THEME.TEXT_SECONDARY }}
          >
            Annulla
          </Button>
          <Button
            onClick={handleCreateOrganization}
            variant="contained"
            disabled={submitting}
            sx={{
              bgcolor: THEME.ACCENT_PRIMARY,
              '&:hover': { bgcolor: alpha(THEME.ACCENT_PRIMARY, 0.8) }
            }}
          >
            {submitting ? <CircularProgress size={24} /> : 'Crea'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !submitting && setDeleteDialogOpen(false)}
        PaperProps={{
          sx: {
            bgcolor: THEME.BG_SECONDARY,
            border: `1px solid ${THEME.BORDER_SUBTLE}`,
            borderRadius: 2
          }
        }}
      >
        <DialogTitle sx={{ color: THEME.TEXT_PRIMARY }}>
          Conferma Eliminazione
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: THEME.TEXT_SECONDARY }}>
            Sei sicuro di voler eliminare l'organizzazione <strong>{selectedOrg?.name}</strong>?
            Questa azione è irreversibile.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => {
              setDeleteDialogOpen(false);
              setSelectedOrg(null);
            }}
            disabled={submitting}
            sx={{ color: THEME.TEXT_SECONDARY }}
          >
            Annulla
          </Button>
          <Button
            onClick={handleDeleteOrganization}
            variant="contained"
            color="error"
            disabled={submitting}
          >
            {submitting ? <CircularProgress size={24} /> : 'Elimina'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
